import { PropsWithChildren, ReactNode, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';

import {
  Box,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
  TypographyProps,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { DialogHeaderMui, useDialog } from './Dialog';
import { FormSubmitButtonsMui as FormSubmitButtons } from './index';

export type ConfirmDialogMuiProps = PropsWithChildren<{
  action?: string;
  confirmDisabled?: boolean;
  expected?: string;
  irreversible?: boolean;
  title?: ReactNode;
  titleProps?: TypographyProps;
  warning?: string;
  onConfirm?: () => Promise<unknown>;
  onClose?: () => void;
}>;

/** @deprecated Legacy MUI copy — migrate consumers to the Encore ConfirmDialog and delete. */
export const ConfirmDialogMui = ({
  action = 'Confirm',
  expected,
  children = null,
  confirmDisabled = false,
  irreversible = false,
  title,
  titleProps,
  warning,
  onConfirm,
  onClose,
}: ConfirmDialogMuiProps) => {
  const { closeDialog } = useDialog();
  const [value, setValue] = useState('');

  const disabled = Boolean(expected && value !== expected);

  const [{ loading, error }, confirm] = useAsyncFn(async () => {
    await onConfirm?.();
    closeDialog();
  }, [onConfirm]);

  const handleClose = () => {
    closeDialog();
    onClose?.();
  };

  return (
    <div data-testid="confirm-dialog" data-track-scope="dialog.confirm">
      <DialogHeaderMui title={title} titleProps={titleProps} />
      <DialogContent>
        {irreversible && (
          <Box marginBottom={2}>
            <Alert severity="error">This action cannot be undone.</Alert>
          </Box>
        )}
        <DialogContentText component="div">{children}</DialogContentText>
        {expected && (
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            value={value}
            name="action"
            inputProps={{ 'aria-label': 'Confirmation' }}
            onChange={e => setValue(e.target.value)}
            onKeyDown={event => event.key === 'Enter' && !disabled && confirm()}
            placeholder={`Enter "${expected}" to confirm this action...`}
           data-track-source-anchor="c914f8c457;confirm-dialog-mui-text-field"/>
        )}

        {error && (
          <Box paddingTop={2}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        )}
        {warning && (
          <Box paddingTop={2}>
            <Alert severity="warning">{warning}</Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <FormSubmitButtons
          onCancel={handleClose}
          onSubmit={confirm}
          label={action}
          disabled={disabled || confirmDisabled}
          loading={loading}
         data-track-source-anchor="394a45d742;confirm-dialog-mui-form-submit-buttons"/>
      </DialogActions>
    </div>
  );
};
